import {
  Component,
  computed,
  DestroyRef,
  type ElementRef,
  inject,
  input,
  type OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  type AbstractControl,
  type ControlValueAccessor,
  NgControl,
  type ValidationErrors,
} from '@angular/forms';

import type { Observable, Subscription } from 'rxjs';

import { controlStateSignal } from '@shared/utils/control-state';

import { Icon } from '../icon/icon';

type FileUploadState = 'idle' | 'attached' | 'uploading' | 'error' | 'disabled';

/**
 * Uploads a file and reports progress as a percentage (0–100), completing when the upload finishes
 * and erroring if it fails. Wire it to a real HTTP upload (`HttpClient` with `reportProgress`), or
 * a stub while there is no backend.
 */
export type FileUploader = (file: File) => Observable<number>;

/** Human-readable file size, e.g. `7.9 MB`, `25 MB`, `420 KB`. */
function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 10) {
    return `${Math.round(mb)} MB`;
  }
  if (mb >= 1) {
    return `${mb.toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Reactive-forms file input with a drag-and-drop dropzone. It validates the chosen file against
 * `accept` and `maxSize`, exposes the `File` through `ControlValueAccessor`, and pushes size/type
 * failures onto its own control so `<app-form-field>` can render the message — while it renders the
 * matching dropzone state (idle / attached / uploading / error / disabled) itself.
 *
 * Provide an `uploader` to run an upload as soon as a valid file is selected: the dropzone shows
 * the "uploading" state with live progress, then "attached" on completion. Without an `uploader` a
 * valid selection attaches immediately. The `uploading`/`progress` inputs still let a parent drive
 * the state externally (e.g. during form submit).
 */
@Component({
  selector: 'app-file-upload',
  imports: [Icon],
  templateUrl: './file-upload.html',
  host: {
    class: 'file-upload',
    '[attr.data-state]': 'state()',
    '[class.is-dragging]': 'dragging()',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
  },
})
export class FileUpload implements ControlValueAccessor, OnInit {
  /** Comma-separated accept list, e.g. `.png,.jpg,.mp4,.pdf,.txt` or `image/*`. */
  readonly accept = input<string>('');

  /** Maximum file size in bytes. Defaults to 25 MB. */
  readonly maxSize = input<number>(25 * 1024 * 1024);

  /** First hint line under the prompt. */
  readonly description = input<string>(
    "Screenshots, screen recordings, or logs. Anything that helps us understand what's happening.",
  );

  /** Second hint line listing accepted formats and the size limit. */
  readonly formatsHint = input<string>('PNG, JPG, MP4, PDF, or TXT. Up to 25 MB.');

  /** Runs an upload on selection; when set, the dropzone shows live progress before attaching. */
  readonly uploader = input<FileUploader | null>(null);

  /** Set by a parent to force the uploading state (with `progress`) externally, e.g. on submit. */
  readonly uploading = input<boolean>(false);

  /** Upload progress percentage (0–100), shown while the `uploading` input is true. */
  readonly progress = input<number>(0);

  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  private readonly ngControl = inject(NgControl, { optional: true, self: true });
  private readonly destroyRef = inject(DestroyRef);

  private readonly controlRef = signal<AbstractControl | null>(null);
  private readonly fieldState = controlStateSignal(this.controlRef);

  protected readonly value = signal<File | null>(null);
  protected readonly disabled = signal(false);
  protected readonly dragging = signal(false);

  // In-flight upload state, driven by the `uploader`.
  private readonly pendingFile = signal<File | null>(null);
  private readonly uploadingInternal = signal(false);
  private readonly progressInternal = signal(0);
  private uploadSub: Subscription | null = null;

  /**
   * Uploading either because a selection is in flight or because the parent forced it. Public so a
   * host form can block submission until the file has finished attaching.
   */
  readonly isUploading = computed(() => this.uploadingInternal() || this.uploading());
  protected readonly progressValue = computed(() =>
    this.uploadingInternal() ? this.progressInternal() : this.progress(),
  );

  protected readonly state = computed<FileUploadState>(() => {
    if (this.disabled()) {
      return 'disabled';
    }
    if (this.isUploading()) {
      return 'uploading';
    }
    if (this.fieldState().showError) {
      return 'error';
    }
    return this.value() ? 'attached' : 'idle';
  });

  // The file to show in the chip: the attached value, or the one currently uploading.
  private readonly displayFile = computed(() => this.value() ?? this.pendingFile());
  protected readonly fileName = computed(() => this.displayFile()?.name ?? '');
  protected readonly fileSize = computed(() => {
    const file = this.displayFile();
    return file ? formatSize(file.size) : '';
  });

  private onChange: (value: File | null) => void = () => {
    /* empty */
  };
  private onTouched: () => void = () => {
    /* empty */
  };

  constructor() {
    // Use NgControl directly (instead of an NG_VALUE_ACCESSOR provider) so this component can read
    // its own validity to drive the error state without a circular dependency.
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.destroyRef.onDestroy(() => this.uploadSub?.unsubscribe());
  }

  ngOnInit(): void {
    this.controlRef.set(this.ngControl?.control ?? null);
  }

  protected open(): void {
    if (this.disabled() || this.isUploading()) {
      return;
    }
    this.fileInput().nativeElement.click();
  }

  protected onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0] ?? null;
    if (file) {
      this.acceptFile(file);
    }
    // Reset so selecting the same file again still fires a change event.
    target.value = '';
  }

  protected onDragOver(event: DragEvent): void {
    if (this.disabled() || this.isUploading()) {
      return;
    }
    event.preventDefault();
    this.dragging.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    // Ignore drags moving between child elements; only reset when leaving the dropzone entirely.
    if (
      event.relatedTarget &&
      (event.currentTarget as Node).contains(event.relatedTarget as Node)
    ) {
      return;
    }
    this.dragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    if (this.disabled() || this.isUploading()) {
      return;
    }
    event.preventDefault();
    this.dragging.set(false);

    const file = event.dataTransfer?.files?.[0] ?? null;
    if (file) {
      this.acceptFile(file);
    }
  }

  protected remove(event: Event): void {
    event.stopPropagation();
    this.cancelUpload();
    this.value.set(null);
    this.onChange(null);
    this.onTouched();
  }

  private acceptFile(file: File): void {
    const control = this.controlRef();
    control?.markAsTouched();
    this.onTouched();
    this.cancelUpload();

    const error = this.validateFile(file);
    if (error) {
      this.value.set(null);
      this.onChange(null);
      // `setErrors` after the value update so the failure survives the validator pass.
      control?.setErrors(error);
      return;
    }

    const uploader = this.uploader();
    if (uploader) {
      this.startUpload(file, uploader);
      return;
    }

    // No uploader: a valid value re-runs validators, clearing any previous size/type error.
    this.value.set(file);
    this.onChange(file);
  }

  private startUpload(file: File, uploader: FileUploader): void {
    // Hold the value back until the upload finishes; the chip shows the pending file meanwhile.
    this.value.set(null);
    this.onChange(null);
    this.pendingFile.set(file);
    this.progressInternal.set(0);
    this.uploadingInternal.set(true);

    this.uploadSub = uploader(file).subscribe({
      next: (percent) => this.progressInternal.set(Math.max(0, Math.min(100, Math.round(percent)))),
      error: () => {
        this.uploadingInternal.set(false);
        this.pendingFile.set(null);
        this.controlRef()?.setErrors({ upload: true });
      },
      complete: () => {
        this.uploadingInternal.set(false);
        this.pendingFile.set(null);
        this.value.set(file);
        this.onChange(file);
      },
    });
  }

  private cancelUpload(): void {
    this.uploadSub?.unsubscribe();
    this.uploadSub = null;
    this.uploadingInternal.set(false);
    this.pendingFile.set(null);
  }

  private validateFile(file: File): ValidationErrors | null {
    const max = this.maxSize();
    if (max && file.size > max) {
      return {
        maxSize: {
          max,
          actual: file.size,
          maxLabel: formatSize(max),
          actualLabel: formatSize(file.size),
        },
      };
    }
    if (!this.matchesAccept(file)) {
      return { fileType: { accept: this.accept() } };
    }
    return null;
  }

  private matchesAccept(file: File): boolean {
    const accept = this.accept().trim();
    if (!accept) {
      return true;
    }

    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();

    return accept
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
      .some((token) => {
        if (token.startsWith('.')) {
          return name.endsWith(token);
        }
        if (token.endsWith('/*')) {
          return type.startsWith(token.slice(0, -1));
        }
        return type === token;
      });
  }

  writeValue(value: File | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
